/** Положение элемента в видимой части области прокрутки после прокрутки. */
export type ScrollAlign = 'start' | 'center' | 'end';

type ScrollOptions = {
  align?: ScrollAlign;
  behavior?: ScrollBehavior;
  /**
   * Область прокрутки, которую двигаем. Без неё берётся ближайший прокручиваемый предок —
   * но он определяется по стилю, а тот у области с отложенной полосой прокрутки становится
   * прокручиваемым не сразу.
   */
  container?: HTMLElement | null;
};

/** Ближайший предок, который реально прокручивается по вертикали. */
const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
    if (isScrollable && parent.scrollHeight > parent.clientHeight) return parent;
    parent = parent.parentElement;
  }
  return null;
};

/**
 * Прокручивает элемент в видимую часть его собственной области прокрутки.
 *
 * Отличие от `scrollIntoView`: двигается только ближайшая прокручиваемая область,
 * внешние области и страница остаются на месте. Иначе прокрутка списка к последнему
 * элементу утаскивает вниз всю страницу вместе с шапкой приложения.
 */
export const scrollIntoViewWithinContainer = (
  element: HTMLElement | null | undefined,
  { align = 'start', behavior = 'auto', container: explicitContainer }: ScrollOptions = {},
): void => {
  if (!element) return;

  const container = explicitContainer ?? findScrollableParent(element);
  if (!container) return;

  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const offset = elementRect.top - containerRect.top + container.scrollTop;

  let top = offset;
  if (align === 'center') top = offset - container.clientHeight / 2 + elementRect.height / 2;
  if (align === 'end') top = offset - container.clientHeight + elementRect.height;

  container.scrollTo({ top, behavior });
};
